/**
 * AddToPurchaseOrderDialog — graduate a Product Dev record into a purchase
 * order.
 *
 * A single Dialog (never modal-from-modal, DESIGN.md §10) with two modes:
 *   - New purchase order: creates a PO at a chosen location for the dev's
 *     supplier (supplier is read-only — it comes from the dev record).
 *   - Existing purchase order: appends a line to an open PO for the same
 *     supplier.
 *
 * The gate is soft (DESIGN.md §4 is for hard, dependent steps; this is
 * advisory). When the record is not yet "ready for bulk", an amber Alert
 * explains it without ever disabling the flow (isReadyForBulk).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ChevronsUpDown, Link2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { useProductDevMutations, useProductDevVariants } from '../hooks/useProductDev';
import { isReadyForBulk } from '../utils/ready-for-bulk';
import { STATUS_LABELS } from '@/features/purchase-orders/utils/status-style';

/** PO statuses that can still receive a new line for graduation. */
const APPENDABLE_STATUSES = [
  'draft',
  'submitted',
  'in_production',
  'ready',
  'partially_received',
] as const;

type Mode = 'new' | 'existing';

interface DialogProduct {
  id: string;
  stage: string;
  status: string;
  price_agreed_by_supplier: boolean;
  price_agreed_by_us: boolean;
  supplier_id: string | null;
  supplier_title: string | null;
  agreed_price: number | null;
  agreed_currency: string | null;
  moq: number | null;
  proposed_quantity: number | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: DialogProduct;
  addToPurchaseOrder: ReturnType<typeof useProductDevMutations>['addToPurchaseOrder'];
  update: ReturnType<typeof useProductDevMutations>['update'];
}

export function AddToPurchaseOrderDialog({
  open,
  onOpenChange,
  product,
  addToPurchaseOrder,
  update,
}: Props) {
  const { t } = useTranslation('productDev');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { locations } = useLocations();

  const ready = isReadyForBulk(product);
  const currency = product.agreed_currency ?? organization?.default_currency ?? 'USD';

  // Buy plan (colour x size). When the record carries planned quantities, offer
  // to graduate the whole matrix as N lines instead of a single quantity.
  const variantsQuery = useProductDevVariants(open ? product.id : undefined);
  const buyPlanLines = useMemo(() => {
    const lines: Array<{ colour: string; size: string; qty: number }> = [];
    for (const v of variantsQuery.data ?? []) {
      for (const size of v.sizes) {
        const qty = v.size_quantities?.[size] ?? 0;
        if (qty > 0) lines.push({ colour: v.colour, size, qty });
      }
    }
    return lines;
  }, [variantsQuery.data]);
  const buyPlanTotal = useMemo(
    () => buyPlanLines.reduce((acc, l) => acc + l.qty, 0),
    [buyPlanLines],
  );
  const buyPlanAvailable = buyPlanLines.length > 0;
  const [useBuyPlan, setUseBuyPlan] = useState(false);
  // Never leave the toggle on when the plan disappears (e.g. record switched).
  const buyPlanActive = useBuyPlan && buyPlanAvailable;

  const [mode, setMode] = useState<Mode>('new');
  const [unitPrice, setUnitPrice] = useState<string>(
    product.agreed_price == null ? '' : String(product.agreed_price),
  );
  const [quantity, setQuantity] = useState<string>(() => {
    const initial = product.proposed_quantity ?? product.moq;
    return initial == null ? '' : String(initial);
  });
  const [locationId, setLocationId] = useState<string>('');
  const [poId, setPoId] = useState<string>('');
  const [poPickerOpen, setPoPickerOpen] = useState(false);

  // Existing-PO options: same supplier, still open.
  const ordersQuery = trpc.purchaseOrders.getOrders.useQuery(
    {},
    { enabled: open && mode === 'existing', staleTime: 60_000, refetchOnWindowFocus: false },
  );

  const poOptions = useMemo(() => {
    const rows = ordersQuery.data?.data ?? [];
    return rows
      .filter(
        (po) =>
          po.supplier_id === product.supplier_id &&
          (APPENDABLE_STATUSES as readonly string[]).includes(po.status),
      )
      .map((po) => ({
        id: po.id,
        label: po.po_number ?? po.title ?? po.id.slice(0, 8),
        status: po.status,
        currency: po.currency ?? null,
      }));
  }, [ordersQuery.data, product.supplier_id]);

  const selectedPo = poOptions.find((po) => po.id === poId) ?? null;

  // Validation (live). In buy-plan mode the single quantity field is replaced
  // by the plan summary, so its validity is derived from the plan total.
  const quantityNum = Number.parseInt(quantity, 10);
  const singleQtyValid = Number.isFinite(quantityNum) && quantityNum > 0;
  const quantityValid = buyPlanActive ? buyPlanTotal > 0 : singleQtyValid;
  // The quantity actually sent: plan grand total when the plan drives the order.
  const sentQuantity = buyPlanActive ? buyPlanTotal : quantityNum;
  const priceNum = Number(unitPrice);
  const priceValid = unitPrice.trim() !== '' && Number.isFinite(priceNum) && priceNum >= 0;
  const locationValid = mode === 'new' ? locationId !== '' : true;
  const poValid = mode === 'existing' ? poId !== '' : true;
  const canSubmit = quantityValid && priceValid && locationValid && poValid;

  // Compare against the currency actually sent (agreed_currency with the
  // org-default fallback), not just agreed_currency — the fallback is also
  // converted server-side and deserves the same warning.
  const currencyMismatch =
    mode === 'existing' &&
    selectedPo?.currency != null &&
    selectedPo.currency !== currency;

  const submitting = addToPurchaseOrder.isPending;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;

    try {
      // The backend is gaining an optional `useBuyPlan` input in parallel. Gate
      // the field so the call compiles whether or not it's landed yet, then cast
      // to the mutation's own input type (the extra key is dropped at the type
      // level but still sent on the wire).
      type AddToPoInput = Parameters<typeof addToPurchaseOrder.mutateAsync>[0];
      const input = {
        productDevId: product.id,
        unitPrice: priceNum,
        currency,
        target:
          mode === 'new'
            ? { mode: 'new', locationId, supplierId: product.supplier_id }
            : { mode: 'existing', poId },
        // The backend refine is an XOR: send EITHER useBuyPlan OR quantity,
        // never both (both fails validation) and never neither. In buy-plan
        // mode the per-size quantities are expanded server-side from the
        // persisted variants — `quantity` must be absent, not undefined.
        ...(buyPlanActive ? { useBuyPlan: true } : { quantity: sentQuantity }),
      } as AddToPoInput;
      const result = await addToPurchaseOrder.mutateAsync(input);

      // Best-effort: persist any edits the user made in the dialog back onto
      // the dev record so the next graduation prefills from the latest values.
      // Non-blocking — a failure here must not derail the success path. Skip the
      // quantity persist-back in buy-plan mode: the grand total is not the
      // record's proposed single-run quantity.
      const priceChanged = priceNum !== (product.agreed_price ?? null);
      const qtyChanged = !buyPlanActive && quantityNum !== (product.proposed_quantity ?? null);
      if (priceChanged || qtyChanged) {
        void update
          .mutateAsync({
            productDevId: product.id,
            updates: {
              ...(priceChanged ? { agreed_price: priceNum } : {}),
              ...(qtyChanged ? { proposed_quantity: quantityNum } : {}),
            },
          })
          .catch(() => {
            /* best-effort persist-back; ignore */
          });
      }

      toast({ title: t('addToPo.toast.added') });
      onOpenChange(false);
      navigate(`/orders/${result.poId}`);
    } catch (err) {
      toast({
        title: t('addToPo.toast.addFailed'),
        description: err instanceof Error ? err.message : t('addToPo.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (submitting ? undefined : onOpenChange(next))}>
      <DialogContent
        className="sm:max-w-lg"
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            void handleSubmit();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            {t('addToPo.title')}
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
              {buyPlanAvailable
                ? t('addToPo.buyPlanBadge', {
                    lines: buyPlanLines.length,
                    units: buyPlanTotal,
                  })
                : t('addToPo.buyPlanBadgeEmpty')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {t('addToPo.description')}
          </DialogDescription>
        </DialogHeader>

        {!ready && (
          <Alert className="border-amber-300 bg-amber-50 text-amber-800 [&>svg]:text-amber-600">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-amber-800">
              {t('addToPo.notReady')}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          {/* Mode */}
          <div className="space-y-2">
            <Label className="text-xs">{t('addToPo.purchaseOrder')}</Label>
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as Mode)}
              className="grid grid-cols-2 gap-2"
            >
              <label
                htmlFor="atpo-mode-new"
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors',
                  mode === 'new'
                    ? 'border-threadheads-purple/50 bg-threadheads-purple/5'
                    : 'border-border hover:bg-muted/40',
                )}
              >
                <RadioGroupItem value="new" id="atpo-mode-new" />
                {t('addToPo.newPo')}
              </label>
              <label
                htmlFor="atpo-mode-existing"
                className={cn(
                  'flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors',
                  mode === 'existing'
                    ? 'border-threadheads-purple/50 bg-threadheads-purple/5'
                    : 'border-border hover:bg-muted/40',
                )}
              >
                <RadioGroupItem value="existing" id="atpo-mode-existing" />
                {t('addToPo.existingPo')}
              </label>
            </RadioGroup>
          </div>

          {/* Buy-plan section — ALWAYS visible so the plan's presence (or
              absence) is legible. When empty the toggle is disabled and the
              subtext explains where to set quantities. */}
          <label
            htmlFor="atpo-usebuyplan"
            className={cn(
              'flex items-start gap-2.5 rounded-md border px-3 py-2.5 transition-colors',
              !buyPlanAvailable
                ? 'cursor-not-allowed border-border bg-muted/20'
                : buyPlanActive
                  ? 'cursor-pointer border-threadheads-purple/50 bg-threadheads-purple/5'
                  : 'cursor-pointer border-border hover:bg-muted/40',
            )}
          >
            <Checkbox
              id="atpo-usebuyplan"
              checked={buyPlanActive}
              disabled={!buyPlanAvailable}
              onCheckedChange={(v) => setUseBuyPlan(v === true)}
              className="mt-0.5"
            />
            <div className="min-w-0">
              <p
                className={cn(
                  'text-sm font-medium',
                  !buyPlanAvailable && 'text-muted-foreground',
                )}
              >
                {t('addToPo.useBuyPlan')}
              </p>
              <p className="text-xs text-muted-foreground tabular-nums">
                {buyPlanAvailable
                  ? t('addToPo.useBuyPlanSummary', {
                      lines: buyPlanLines.length,
                      total: buyPlanTotal,
                    })
                  : t('addToPo.buyPlanEmptyHint')}
              </p>
            </div>
          </label>

          {/* Shared fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="atpo-price" className="text-xs">
                {t('addToPo.unitPrice')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="atpo-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={unitPrice}
                  onChange={(e) => setUnitPrice(e.target.value)}
                  className="pr-12"
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground tabular-nums">
                  {currency}
                </span>
              </div>
              {!priceValid && unitPrice.trim() !== '' && (
                <p className="text-sm text-destructive">{t('addToPo.errUnitPrice')}</p>
              )}
            </div>
            {!buyPlanActive && (
              <div className="space-y-1">
                <Label htmlFor="atpo-qty" className="text-xs">
                  {t('addToPo.quantity')}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Input
                  id="atpo-qty"
                  type="number"
                  min={1}
                  step={1}
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
                {!singleQtyValid && quantity.trim() !== '' && (
                  <p className="text-sm text-destructive">{t('addToPo.errQuantity')}</p>
                )}
              </div>
            )}
          </div>

          {/* Buy-plan line summary (read-only) — replaces the single qty field. */}
          {buyPlanActive && (
            <div className="space-y-1">
              <div className="flex items-baseline justify-between">
                <Label className="text-xs">{t('addToPo.buyPlanLines')}</Label>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t('addToPo.buyPlanGrandTotal', { total: buyPlanTotal })}
                </span>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-md border divide-y">
                {buyPlanLines.map((l, i) => (
                  <div
                    key={`${l.colour}-${l.size}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-sm"
                  >
                    <span className="truncate">
                      {l.colour}
                      <span className="text-muted-foreground"> · {l.size}</span>
                    </span>
                    <span className="shrink-0 tabular-nums font-medium">{l.qty}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* New mode: supplier chip + location */}
          {mode === 'new' && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">{t('addToPo.supplier')}</Label>
                {product.supplier_title ? (
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground">
                    {product.supplier_title}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('addToPo.noSupplier')}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="atpo-location" className="text-xs">
                  {t('addToPo.location')}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger
                    id="atpo-location"
                    className={cn(
                      locationId === '' && 'border-amber-300 ring-1 ring-amber-100/50',
                    )}
                  >
                    <SelectValue placeholder={t('addToPo.chooseLocation')} />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {locationId === '' && (
                  <p className="text-xs text-amber-700">{t('addToPo.chooseLocationHint')}</p>
                )}
              </div>
            </>
          )}

          {/* Existing mode: searchable PO combobox */}
          {mode === 'existing' && (
            <div className="space-y-1">
              <Label className="text-xs">
                {t('addToPo.purchaseOrder')}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Popover open={poPickerOpen} onOpenChange={setPoPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={poPickerOpen}
                    className={cn(
                      'w-full justify-between font-normal',
                      poId === '' && 'border-amber-300 ring-1 ring-amber-100/50',
                      poId === '' && 'text-muted-foreground',
                    )}
                    disabled={ordersQuery.isLoading}
                  >
                    {ordersQuery.isLoading ? (
                      <span className="inline-flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('addToPo.loadingOrders')}
                      </span>
                    ) : selectedPo ? (
                      <span className="inline-flex items-center gap-2 truncate">
                        <span className="truncate">{selectedPo.label}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">
                          {STATUS_LABELS[selectedPo.status] ?? selectedPo.status}
                        </span>
                      </span>
                    ) : (
                      t('addToPo.choosePo')
                    )}
                    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder={t('addToPo.searchOrders')} />
                    <CommandList>
                      <CommandEmpty>
                        {product.supplier_id
                          ? t('addToPo.noOpenPos')
                          : t('addToPo.noSupplierMatch')}
                      </CommandEmpty>
                      {poOptions.map((po) => (
                        <CommandItem
                          key={po.id}
                          value={`${po.label} ${po.status}`}
                          onSelect={() => {
                            setPoId(po.id);
                            setPoPickerOpen(false);
                          }}
                          className="gap-2"
                        >
                          <Check
                            className={cn(
                              'h-3.5 w-3.5 shrink-0',
                              po.id === poId ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          <span className="flex-1 min-w-0 truncate">{po.label}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {STATUS_LABELS[po.status] ?? po.status}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {poId === '' && (
                <p className="text-xs text-amber-700">{t('addToPo.choosePoHint')}</p>
              )}
              {currencyMismatch && selectedPo?.currency && (
                <p className="text-xs text-muted-foreground">
                  {t('addToPo.currencyConverted', { currency: selectedPo.currency })}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('addToPo.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit || submitting}
            className="h-9 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> {t('addToPo.adding')}
              </>
            ) : (
              <>
                <Link2 className="h-3.5 w-3.5 mr-1.5" /> {t('addToPo.addToPo')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
